import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useTranslations } from '@lang';
import { Button } from '@common/components';
import Error from '@common/components/Error';
import { BlockInputField } from '@common/components/FormElements/BlockInputField';
import { RadioGroupFlexField } from '@common/components/RadioGroupFlexField';
import { Spinner } from '@common/components/Spinner';
import { FormRow, FormSuccessMessage } from '@common/components/AuthLayout/Layout';

import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { InfoIcon } from '@root/styles/icons';
import { Popover } from 'react-tiny-popover';

import { Form, InlineWrapper, PopoverMessage } from './RegisterForm.styles';
import { EMAIL_REGEXP } from '@utils/validators';
import { getErrorFromFieldState } from '@utils/formHelpers';

const buildRegisterSchema = (spaIntegrated: boolean) =>
  z
    .object({
      email: z.string().min(1, 'required').regex(EMAIL_REGEXP, 'email'),
      alreadySpaMember: z.string().optional(),
      externalSpaId: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (spaIntegrated) {
        if (!data.alreadySpaMember) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'required',
            path: ['alreadySpaMember'],
          });
        }
        if (data.alreadySpaMember === 'yes') {
          if (!data.externalSpaId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['externalSpaId'],
            });
          } else if (isNaN(Number(data.externalSpaId))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'invalid_number',
              path: ['externalSpaId'],
            });
          }
        }
      }
    });

const radioOptions = [
  { label: 'Yes', value: 'yes', id: 'register.yes' as const },
  { label: 'No', value: 'no', id: 'register.no' as const },
];

type RegisterFormProps = {
  onSubmit: (data: Record<string, string>) => void;
  spaIntegrated?: boolean;
};

const RegisterForm = ({ onSubmit, spaIntegrated = false }: RegisterFormProps) => {
  const { t } = useTranslations();

  const schema = useMemo(() => buildRegisterSchema(spaIntegrated), [spaIntegrated]);

  const {
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', alreadySpaMember: '', externalSpaId: '' },
    mode: 'onChange',
  });

  const emailValue = watch('email');
  const alreadySpaMemberValue = watch('alreadySpaMember');
  const externalSpaIdValue = watch('externalSpaId');

  const handleIsSpaMemberChange = (value: string) => {
    setValue('alreadySpaMember', value, { shouldValidate: true, shouldDirty: true });
    clearErrors('alreadySpaMember');
    if (value === 'no') {
      setValue('externalSpaId', '', { shouldValidate: false, shouldDirty: true });
      clearErrors('externalSpaId');
    }
  };

  const handleFormSubmit = async (data: {
    email: string;
    alreadySpaMember?: string;
    externalSpaId?: string;
  }) => {
    setRegisterError('');
    const submitData: Record<string, string> = { email: data.email };
    if (spaIntegrated && data.alreadySpaMember === 'yes') {
      submitData.alreadySpaMember = data.alreadySpaMember;
      submitData.externalSpaId = data.externalSpaId || '';
    } else if (spaIntegrated && data.alreadySpaMember) {
      submitData.alreadySpaMember = data.alreadySpaMember;
    }

    try {
      await onSubmit(submitData);
      setIsSubmitSuccessful(true);
    } catch (e) {
      setRegisterError('registrationFailed');
    }
  };

  const [registerError, setRegisterError] = useState('');
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const togglePopoverOpen = () => setPopoverIsOpen(!popoverIsOpen);

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Spinner isActive={isSubmitting} showDelayMessage />
      {!isSubmitSuccessful && (
        <>
          <FormRow>
            <BlockInputField
              name="email"
              label={t('register.emailLabel')}
              isRequired
              value={emailValue}
              onChange={(e) =>
                setValue('email', e.target.value, { shouldValidate: true, shouldDirty: true })
              }
              error={getErrorFromFieldState(errors.email)}
            />
          </FormRow>
          {spaIntegrated && (
            <FormRow>
              <RadioGroupFlexField
                options={radioOptions}
                name="alreadySpaMember"
                input={{
                  value: alreadySpaMemberValue,
                  onChange: handleIsSpaMemberChange as React.Dispatch<React.SetStateAction<string>>,
                }}
                meta={{
                  error: (errors.alreadySpaMember
                    ? [{ code: errors.alreadySpaMember.message || 'required' }]
                    : undefined) as unknown as string,
                  touched: !!errors.alreadySpaMember,
                }}
                error={
                  (errors.alreadySpaMember
                    ? [{ code: errors.alreadySpaMember.message || 'required' }]
                    : false) as unknown as boolean
                }
                label={t('register.alreadySpaMember')}
                isRequired
              />
            </FormRow>
          )}
          {spaIntegrated && alreadySpaMemberValue === 'yes' && (
            <FormRow>
              <BlockInputField
                name="externalSpaId"
                label={
                  <InlineWrapper>
                    {t('register.uniqueId')}
                    <Popover
                      isOpen={popoverIsOpen}
                      onClickOutside={() => setPopoverIsOpen(false)}
                      content={<PopoverMessage>{t('register.popoverMessage')}</PopoverMessage>}
                    >
                      <InfoIcon
                        onClick={togglePopoverOpen}
                        cursor="pointer"
                        width="16"
                        height="16"
                      />
                    </Popover>
                  </InlineWrapper>
                }
                isRequired
                value={externalSpaIdValue}
                onChange={(e) =>
                  setValue('externalSpaId', e.target.value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                error={getErrorFromFieldState(errors.externalSpaId)}
              />
            </FormRow>
          )}
          <FormRow>
            <Button type="submit" disabled={isSubmitting} isPrimary>
              {t('register.register')}
            </Button>
          </FormRow>
          {registerError && (
            <FormRow>
              <Error error={{ code: registerError }} />
            </FormRow>
          )}
        </>
      )}
      {isSubmitSuccessful && (
        <>
          <FormRow>
            <FormSuccessMessage>{t('register.successMessage')}</FormSuccessMessage>
          </FormRow>
          <FormRow>
            <Link route={Routes.HOME}>
              <Button disabled={isSubmitting}>{t('register.backToHomepage')}</Button>
            </Link>
          </FormRow>
        </>
      )}
    </Form>
  );
};

export default RegisterForm;
